import clsx from 'clsx';
import React, {useEffect} from 'react';

import {Tooltip} from 'common/components/tooltip/tooltip';
import {SvgIconReact} from 'common/components/svg-icon';
import {Placement} from 'common/components/tooltip/types';
import {ButtonType} from 'common/components/button/constants';
import {ButtonReact} from 'common/components/button';
import {react2panel} from 'common/util/react2panel-nocontext';
import {formatTextReact} from 'common/util/format-text-react';
import {withStyles} from 'common/util/react';

import {ButtonTheme} from '../button/constants';
import {createBannerShownEventFor, getIcon, getImage} from './helpers';
import {
  ActionEvent,
  BannerAction,
  BannerReactProps,
  BannerSize,
  BannerTheme,
  BannerTooltip,
  BannerType,
  BUTTON_TYPE_FROM_THEME,
  CallToAction,
} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

const TRACK_BANNER_CTA_CLICK = `[mp-banner] Banner CTA Clicked`;

const BodyTextClassName = styles.bannerBody;

function getTooltip(bannerTooltip: BannerTooltip) {
  return bannerTooltip ? (
    <div className={styles.bannerTooltip}>
      <Tooltip
        placement={Placement.Top}
        maxWidth={160}
        content={<div className={`banner-info-tooltip`}>{bannerTooltip.tooltipText}</div>}
      >
        <a href={bannerTooltip.href}>{bannerTooltip.text}</a>
      </Tooltip>
    </div>
  ) : null;
}

export function CoreBanner({
  tooltip,
  ctaNode,
  textNode,
  bodyText = ``,
  theme = BannerTheme.Info,
  type = BannerType.Floating,
  imgSrc,
  headerText,
  size = BannerSize.Medium,
  iconNode,
  icon = `from-theme`,
  showCloseButton,
  noWrapContent,
  footerText,
  style,
  className,
  elref,
  onAction,
  id,
}: BannerReactProps): React.ReactNode {
  const tooltipNode = tooltip ? getTooltip(tooltip) : null;

  const bodyTextNode: React.ReactNode = (textNode || bodyText) && (
    <div elref={`bannerBodyText`} className={clsx(BodyTextClassName, styles[`${theme}Theme`])}>
      {textNode ? textNode : formatTextReact(bodyText, {}, tooltipNode ? [tooltipNode] : [])}
    </div>
  );
  const handleCloseButtonClick = (): void => {
    onAction?.(new CustomEvent(`action`, {detail: {actionName: BannerAction.Dismiss}}) as ActionEvent);
  };

  const themeIcon = getIcon(icon, theme);

  return (
    <div
      className={clsx(`mp-banner-wrapper`, className, styles.bannerWrapper, {
        [styles[`${type}Type`]]: true,
        [styles[`${theme}Theme`]]: true,
        [styles.hasImage]: !!imgSrc,
      })}
      style={style}
      elref={elref}
      id={id}
    >
      <div
        className={clsx(styles.bannerPanel, {
          [styles.hasCta]: !!ctaNode,
          [styles[`${size}Size`]]: true,
          [styles[`${theme}Theme`]]: true,
          [styles[`${type}Type`]]: true,
          [styles.hasImage]: !!imgSrc,
          [styles.bodyOnly]: Boolean(bodyTextNode) && !headerText,
        })}
      >
        <div className={styles.bannerIconWrapper}>
          {getImage(imgSrc, theme) ? (
            <img className={styles.bannerImage} src={getImage(imgSrc, theme)} />
          ) : iconNode ? (
            <div className={clsx(styles.bannerTitleIcon, {[`banner-copy-no-header`]: !headerText})}>{iconNode}</div>
          ) : themeIcon ? (
            <SvgIconReact
              className={clsx(styles.bannerTitleIcon, {
                [`banner-copy-no-header`]: !headerText,
              })}
              icon={themeIcon}
            />
          ) : null}
          {showCloseButton ? (
            <ButtonReact theme={ButtonTheme.Tertiary} icon={`x`} onClick={() => handleCloseButtonClick()} />
          ) : null}
        </div>

        <div
          className={clsx(styles.bannerContent, {
            [styles[`${theme}Theme`]]: true,
            nowrap: noWrapContent,
          })}
        >
          <div
            className={clsx(styles.bannerCopy, {
              [`banner-copy-no-header`]: !headerText,
              [styles[`${theme}Theme`]]: true,
            })}
          >
            {headerText && <header className={clsx(styles.bannerHeader, styles[`${theme}Theme`])}>{headerText}</header>}
            {bodyTextNode}
            {footerText && <footer className={styles.bannerFooter}>{footerText}</footer>}
          </div>
          {ctaNode ? <div className={clsx(styles.bannerActions, styles[`${theme}Theme`])}>{ctaNode}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function BannerReact(props: BannerReactProps) {
  const {
    bodyText,
    bannerTrackingType,
    theme = BannerTheme.Info,
    ctas,
    type = BannerType.Floating,
    size = BannerSize.Medium,
    ctaNode,
    onAction,
    onTrack,
  } = props;
  useEffect(() => {
    requestAnimationFrame(() => {
      onTrack?.(createBannerShownEventFor(bannerTrackingType, bodyText));
    });
  }, [bannerTrackingType, bodyText, onTrack]);

  const trackBannerCtaClick = (cta: CallToAction) => {
    const {actionName, href, text, trackEventName, trackProps} = cta;

    if (trackEventName) {
      // Don't break legacy tracking for these banners
      onTrack?.(
        new CustomEvent(`track`, {
          bubbles: true,
          composed: true,
          cancelable: true,
          detail: {
            eventName: trackEventName,
            props: trackProps || {
              'Action Name': actionName,
            },
          },
        }),
      );
    }

    // New tracking: "[mp-banner] Banner CTA Clicked" event
    onTrack?.(
      new CustomEvent(`track`, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: {
          eventName: TRACK_BANNER_CTA_CLICK,
          props: {
            'Action Name': actionName,
            'Banner Type': bannerTrackingType || bodyText,
            href,
            text,
            ...(trackProps || {}),
          },
        },
      }),
    );
  };

  const getButtonType = (): ButtonType => BUTTON_TYPE_FROM_THEME.get(theme as BannerTheme) || ButtonType.Base;

  const handleCtaButtonClick = (cta: CallToAction): void => {
    const {actionName, href, hrefTarget, download} = cta;

    if (actionName && actionName !== BannerAction.None) {
      onAction?.(new CustomEvent(`action`, {detail: {actionName}}) as ActionEvent);
    }

    trackBannerCtaClick(cta);

    // if user wishes to make a link button
    if (href) {
      if (download) {
        const tempAnchor = document.createElement(`a`);
        tempAnchor.href = href;
        tempAnchor.download = ``;
        tempAnchor.target = `_blank`;

        document.body.appendChild(tempAnchor);
        tempAnchor.click();
        document.body.removeChild(tempAnchor);
      } else {
        window.open(href, hrefTarget || `_blank`);
      }
    }
  };

  const hasCta = ctas && ctas.length > 0;
  const ctaNodes = ctaNode ? (
    ctaNode
  ) : hasCta ? (
    <>
      {ctas.map((cta, i) => {
        const isEmbedded = type === BannerType.Embedded;
        const isSmall = size === BannerSize.Small;
        const embeddedTheme = isSmall ? ButtonTheme.Tertiary : ButtonTheme.Secondary;
        const shownTheme = cta.theme || (isEmbedded ? embeddedTheme : undefined);
        const shouldShowXIcon = isSmall && cta.actionName === BannerAction.Dismiss;

        return (
          <ButtonReact
            className={clsx(`banner-cta-button`, styles.ctaButton, {
              [`banner-action-button-embedded`]: isEmbedded,
              [styles[`${theme}Theme`]]: true,
            })}
            theme={shownTheme}
            icon={shouldShowXIcon ? `x` : cta.icon}
            type={getButtonType()}
            elref={cta.elref}
            onClick={() => handleCtaButtonClick(cta)}
            key={`${cta.elref}-${i}`}
          >
            {!shouldShowXIcon ? cta.text : null}
          </ButtonReact>
        );
      })}
    </>
  ) : null;
  return <CoreBanner {...props} ctaNode={ctaNodes} />;
}

export const BannerEl = react2panel(`mp-banner`, withStyles(BannerReact, css));
